import * as React from 'react'
import { useState, useMemo } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { useAuth } from '../hooks/useAuth'
import { useLocale } from '../contexts/LocaleContext'
import type { Locale } from '../contexts/LocaleContext'
import { useUser, useOwnUser, useUpdateUser, useUpdateOwnProfile, queryKeys } from '../hooks/useQueries'
import { useEmailSignature, EMAIL_SIGNATURE_MAX_LENGTH } from '../hooks/useEmailSignature'
import { useBreadcrumbs } from '../hooks/useBreadcrumbs'
import { LocaleSelector } from './LocaleSelector'
import { EntityPicture } from './EntityPicture'
import { useTranslation } from 'react-i18next'
import { PageHeader } from './shared/PageHeader'
import { SECTION_ICONS } from '../lib/section-icons'

interface UserProfilePageProps {
    orgCode: string
    userUid?: string // Optional - if not provided, uses current authenticated user
}

interface UserProperties {
    firstName?: string
    lastName?: string
    phone?: string
    language?: string
    // Personne physique — minimal civil-status attributes.
    dateOfBirth?: string
    placeOfBirth?: string
}

export function UserProfilePage({ orgCode, userUid: propUserUid }: UserProfilePageProps) {
    const { user: authUser, loading: authLoading, updateAuthUser } = useAuth()
    const { setLocale } = useLocale()
    const queryClient = useQueryClient()
    const { t } = useTranslation('users')

    // Use provided userUid or fall back to authenticated user's UID
    const userUid = propUserUid || authUser?.uid || ''
    const isOwnProfile = !propUserUid || propUserUid === authUser?.uid

    // The own profile reads and writes through /users/me (spec 2026-10-23-05),
    // on `organization.read`: a member without `contacts` permissions can
    // still edit their name and language. A colleague's stays on /users/{uid}.
    const ownUser = useOwnUser(orgCode, { enabled: isOwnProfile })
    const otherUser = useUser(orgCode, userUid, { enabled: !isOwnProfile })
    const { data: userData, isLoading } = isOwnProfile ? ownUser : otherUser
    const updateUserMutation = useUpdateUser(orgCode, userUid)
    const updateOwnProfileMutation = useUpdateOwnProfile(orgCode)
    const isSavingProfile = updateUserMutation.isPending || updateOwnProfileMutation.isPending
    // The signature is a user SETTING, not a property: it is read and written
    // through /users/me/settings/email_signature (spec 2026-09-15-02 §3), so it
    // travels beside the profile form rather than inside its payload.
    const { signature: storedSignature, saveSignature, isSaving: isSavingSignature } = useEmailSignature(orgCode, userUid, isOwnProfile)

    const [firstName, setFirstName] = useState('')
    const [lastName, setLastName] = useState('')
    const [email, setEmail] = useState('')
    const [phoneNumber, setPhoneNumber] = useState('')
    const [dateOfBirth, setDateOfBirth] = useState('')
    const [placeOfBirth, setPlaceOfBirth] = useState('')
    const [language, setLanguage] = useState('en')
    const [emailSignature, setEmailSignature] = useState('')
    const [saveError, setSaveError] = useState<string | null>(null)
    const [saveSuccess, setSaveSuccess] = useState(false)

    // Set breadcrumbs
    const breadcrumbs = useMemo(() => {
        if (isOwnProfile) {
            return [
                { title: t('breadcrumb.home'), path: `/${orgCode}` },
                { title: t('breadcrumb.user'), path: `/${orgCode}/user/me/profile` }
            ]
        } else {
            return [
                { title: t('breadcrumb.home'), path: `/${orgCode}` },
                { title: t('breadcrumb.contacts'), path: `/${orgCode}/users` },
                { title: userData?.name || t('breadcrumb.user'), path: `/${orgCode}/users/${userUid}` },
                { title: t('breadcrumb.settings') }
            ]
        }
    }, [orgCode, isOwnProfile, userUid, userData?.name, t])

    useBreadcrumbs(breadcrumbs)

    // Effect to update form fields when userData changes
    React.useEffect(() => {
        if (userData) {
            setEmail(userData.email || '')

            // Use properties if available, otherwise parse name
            const properties = userData.properties as UserProperties
            // Civil-status attributes (and phone, written through the same
            // definition-backed `phone` key the party page's identity card
            // uses) load regardless of the name branch below — a contact
            // created without firstName/lastName properties must still show
            // its phone here.
            setDateOfBirth(properties?.dateOfBirth || '')
            setPlaceOfBirth(properties?.placeOfBirth || '')
            setPhoneNumber(properties?.phone || '')
            // The language loads on its own: the top-bar toggle saves it
            // without firstName/lastName, and a Save must not reset it.
            setLanguage(properties?.language || 'en')
            if (properties?.firstName || properties?.lastName) {
                setFirstName(properties.firstName || '')
                setLastName(properties.lastName || '')
            } else if (userData.name) {
                const nameParts = userData.name.trim().split(' ')
                if (nameParts.length > 1) {
                    setFirstName(nameParts.slice(0, -1).join(' '))
                    setLastName(nameParts[nameParts.length - 1])
                } else {
                    setFirstName(nameParts[0] || '')
                    setLastName('')
                }
            }
        }
    }, [userData])

    // The signature loads on its own request, so it gets its own sync — the
    // prop-to-state rule of front/CLAUDE.md rather than a useState initializer,
    // which would freeze the empty first render.
    React.useEffect(() => {
        setEmailSignature(storedSignature)
    }, [storedSignature])

    // Show loading state while auth is loading
    if (authLoading || !authUser) {
        return (
            <div className="bg-card rounded-lg shadow-lg p-6">
                <div className="flex items-center justify-center">
                    <div className="motion-safe:animate-spin rounded-full h-6 w-6 border-b-2 border-primary mr-3"></div>
                    <span className="text-muted-foreground">{t('profile.loading_auth')}</span>
                </div>
            </div>
        )
    }

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault()
        setSaveError(null)
        setSaveSuccess(false)

        try {
            const fullName = `${firstName.trim()} ${lastName.trim()}`

            if (isOwnProfile) {
                await updateOwnProfileMutation.mutateAsync({
                    name: fullName,
                    properties: {
                        firstName: firstName.trim(),
                        lastName: lastName.trim(),
                        phone: phoneNumber.trim(),
                        language: language as Locale,
                        dateOfBirth: dateOfBirth.trim() || null,
                        placeOfBirth: placeOfBirth.trim() || null
                    }
                })
                // The address is a manager's field (PATCH /users/{uid},
                // contacts.write): sent only when it actually changed.
                if (email !== (userData?.email || '')) {
                    await updateUserMutation.mutateAsync({ email })
                    queryClient.invalidateQueries({ queryKey: ['get', '/api/v1/organizations/{orgCode}/users/me'] })
                }
            } else {
                await updateUserMutation.mutateAsync({
                    name: fullName,
                    email: email,
                    properties: {
                        ...(userData?.properties as UserProperties ?? {}),
                        // TODO(spec 2026-09-10-02): firstName/lastName/language/
                        // dateOfBirth/placeOfBirth are still informal camelCase
                        // keys written by bypassing the property definition
                        // system, exactly like phoneNumber used to be. `phone`
                        // was converted to the definition-backed key; the rest
                        // are out of scope here but should follow the same path.
                        firstName: firstName.trim(),
                        lastName: lastName.trim(),
                        phone: phoneNumber.trim(),
                        language: language,
                        dateOfBirth: dateOfBirth.trim() || undefined,
                        placeOfBirth: placeOfBirth.trim() || undefined
                    } as any
                })
            }

            // The signature is written only when it actually changed: an
            // unchanged profile save must not PUT a setting (and, for a
            // colleague's profile, must not need `contacts.write` it did not
            // need before).
            if (emailSignature !== storedSignature) {
                await saveSignature(emailSignature)
            }

            setSaveSuccess(true)

            // Update auth context user in-place so LocaleContext picks up the new language
            if (authUser && authUser.uid === userData?.uid) {
                updateAuthUser({
                    name: fullName,
                    properties: { firstName: firstName.trim(), lastName: lastName.trim(), language }
                })
                if (language === 'en' || language === 'fr') {
                    setLocale(language as Locale)
                }
            }
        } catch (error) {
            console.error('Failed to update user:', error)
            setSaveError((error as any)?.title || (error as any)?.message || 'Failed to update profile')
        }
    }

    const isFieldsDisabled = authLoading || isLoading || !userData
    // Birth date/place are personne-physique attributes — hide them for
    // companies (personne morale) and group nodes.
    const userType = (userData as { type?: string } | undefined)?.type
    const isPhysicalPerson = userType !== 'company' && userType !== 'user_group'

    return (
        <div className="bg-card rounded-lg shadow-lg">
                <div className="px-6 py-4 border-b border-border">
                    <PageHeader
                        icon={SECTION_ICONS.profile}
                        title={isOwnProfile ? t('profile.title_own') : t('profile.title_other', { name: userData?.name || '' })}
                        description={isOwnProfile ? t('profile.subtitle_own') : t('profile.subtitle_other') }
                    />
                </div>

                <div className="p-6">
                    {(authLoading || isLoading) && (
                        <div className="flex items-center justify-center mb-6">
                            <div className="motion-safe:animate-spin rounded-full h-6 w-6 border-b-2 border-primary mr-3"></div>
                            <span className="text-muted-foreground">
                                {authLoading ? t('profile.loading_auth') : t('profile.loading_profile')}
                            </span>
                        </div>
                    )}

                    <form onSubmit={handleSubmit} className="space-y-6">
                        {saveError && (
                            <div className="bg-destructive/10 border border-destructive/20 rounded-md p-4">
                                <div className="text-destructive text-sm">{saveError}</div>
                            </div>
                        )}

                        {saveSuccess && (
                            <div className="bg-green-500/10 border border-green-500/20 rounded-md p-4" data-testid="profile-save-success">
                                <div className="text-green-600 dark:text-green-400 text-sm">{t('profile.save_success')}</div>
                            </div>
                        )}

                        {/* Profile Picture Section */}
                        <div>
                            <label className="block text-sm font-medium text-foreground mb-2">
                                {t('profile.profile_picture_label')}
                            </label>
                            <div className="flex items-start space-x-4">
                                <EntityPicture
                                    entityType="user"
                                    // Own avatar goes through /users/me/picture,
                                    // gated on `organization.read` instead of
                                    // `contacts.read` (spec 2026-09-06-04).
                                    entityId={isOwnProfile ? 'me' : userData?.uid || ''}
                                    documentUid={userData?.picture?.document_uid}
                                    orgCode={orgCode}
                                    size="128x128"
                                    editable={!isFieldsDisabled}
                                    className="w-24 h-24"
                                    alt={`${firstName} ${lastName} profile picture`}
                                    onUpload={(success, error) => {
                                        if (success) {
                                            console.log('Profile picture updated successfully');
                                            setSaveSuccess(true);
                                            // Invalidate user cache to refetch updated picture data
                                            queryClient.invalidateQueries({
                                                queryKey: queryKeys.user(orgCode, userUid)
                                            });
                                        } else {
                                            console.error('Failed to update profile picture:', error);
                                            setSaveError(error || 'Failed to update profile picture');
                                        }
                                    }}
                                />
                                <div className="text-sm text-muted-foreground">
                                    <p>{t('profile.profile_picture_hint')}</p>
                                    <p>{t('profile.profile_picture_formats')}</p>
                                    <p>{t('profile.profile_picture_max_size')}</p>
                                </div>
                            </div>
                        </div>

                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                            <div>
                                <label htmlFor="firstName" className="block text-sm font-medium text-foreground mb-2">
                                    {t('profile.first_name')}
                                </label>
                                <input
                                    type="text"
                                    id="firstName"
                                    value={firstName}
                                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => setFirstName(e.target.value)}
                                    placeholder={authLoading || isLoading ? t('profile.loading_placeholder') : t('profile.first_name_placeholder')}
                                    required
                                    disabled={isFieldsDisabled}
                                    className={`block w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-ring focus:border-ring ${isFieldsDisabled
                                        ? 'border-border bg-muted text-muted-foreground cursor-not-allowed'
                                        : 'border-border bg-background text-foreground'
                                        }`}
                                />
                            </div>

                            <div>
                                <label htmlFor="lastName" className="block text-sm font-medium text-foreground mb-2">
                                    {t('profile.last_name')}
                                </label>
                                <input
                                    type="text"
                                    id="lastName"
                                    value={lastName}
                                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => setLastName(e.target.value)}
                                    placeholder={authLoading || isLoading ? t('profile.loading_placeholder') : t('profile.last_name_placeholder')}
                                    required
                                    disabled={isFieldsDisabled}
                                    className={`block w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-ring focus:border-ring ${isFieldsDisabled
                                        ? 'border-border bg-muted text-muted-foreground cursor-not-allowed'
                                        : 'border-border bg-background text-foreground'
                                        }`}
                                />
                            </div>
                        </div>

                        <div>
                            <label htmlFor="phoneNumber" className="block text-sm font-medium text-foreground mb-2">
                                {t('profile.phone_number')}
                            </label>
                            <input
                                type="tel"
                                id="phoneNumber"
                                value={phoneNumber}
                                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setPhoneNumber(e.target.value)}
                                placeholder={authLoading || isLoading ? t('profile.loading_placeholder') : t('profile.phone_number_placeholder')}
                                disabled={isFieldsDisabled}
                                className={`block w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-ring focus:border-ring ${isFieldsDisabled
                                    ? 'border-border bg-muted text-muted-foreground cursor-not-allowed'
                                    : 'border-border bg-background text-foreground'
                                    }`}
                            />
                        </div>

                        {isPhysicalPerson && (
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                <div>
                                    <label htmlFor="dateOfBirth" className="block text-sm font-medium text-foreground mb-2">
                                        {t('profile.date_of_birth')}
                                    </label>
                                    <input
                                        type="date"
                                        id="dateOfBirth"
                                        value={dateOfBirth}
                                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => setDateOfBirth(e.target.value)}
                                        disabled={isFieldsDisabled}
                                        data-testid="date-of-birth-input"
                                        className={`block w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-ring focus:border-ring ${isFieldsDisabled
                                            ? 'border-border bg-muted text-muted-foreground cursor-not-allowed'
                                            : 'border-border bg-background text-foreground'
                                            }`}
                                    />
                                </div>
                                <div>
                                    <label htmlFor="placeOfBirth" className="block text-sm font-medium text-foreground mb-2">
                                        {t('profile.place_of_birth')}
                                    </label>
                                    <input
                                        type="text"
                                        id="placeOfBirth"
                                        value={placeOfBirth}
                                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => setPlaceOfBirth(e.target.value)}
                                        placeholder={t('profile.place_of_birth_placeholder')}
                                        disabled={isFieldsDisabled}
                                        data-testid="place-of-birth-input"
                                        className={`block w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-ring focus:border-ring ${isFieldsDisabled
                                            ? 'border-border bg-muted text-muted-foreground cursor-not-allowed'
                                            : 'border-border bg-background text-foreground'
                                            }`}
                                    />
                                </div>
                            </div>
                        )}

                        <div>
                            <label htmlFor="locale" className="block text-sm font-medium text-foreground mb-2">
                                {t('profile.language')}
                            </label>
                            <LocaleSelector
                                value={language}
                                onChange={setLanguage}
                                disabled={isFieldsDisabled}
                            />
                        </div>

                        <div>
                            <label htmlFor="emailSignature" className="block text-sm font-medium text-foreground mb-2">
                                {t('profile.email_signature')}
                            </label>
                            <textarea
                                id="emailSignature"
                                rows={4}
                                value={emailSignature}
                                onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => setEmailSignature(e.target.value)}
                                placeholder={t('profile.email_signature_placeholder')}
                                maxLength={EMAIL_SIGNATURE_MAX_LENGTH}
                                disabled={isFieldsDisabled}
                                data-testid="email-signature-input"
                                className={`block w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-ring focus:border-ring ${isFieldsDisabled
                                    ? 'border-border bg-muted text-muted-foreground cursor-not-allowed'
                                    : 'border-border bg-background text-foreground'
                                    }`}
                            />
                            <p className="text-sm text-muted-foreground mt-1">
                                {t('profile.email_signature_hint')}
                            </p>
                        </div>

                        <div>
                            <label htmlFor="email" className="block text-sm font-medium text-foreground mb-2">
                                {t('profile.email')}
                            </label>
                            <input
                                type="email"
                                id="email"
                                value={email}
                                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setEmail(e.target.value)}
                                placeholder={authLoading || isLoading ? t('profile.loading_placeholder') : t('profile.email_placeholder')}
                                required
                                disabled={true} // Always disabled as mentioned in original code
                                className="block w-full px-3 py-2 border border-border rounded-md shadow-sm bg-muted text-muted-foreground cursor-not-allowed focus:outline-none focus:ring-2 focus:ring-ring focus:border-ring"
                            />
                            <p className="text-sm text-muted-foreground mt-1">
                                {t('profile.email_readonly_note')}
                            </p>
                        </div>

                        <div className="flex justify-end">
                            <button
                                type="submit"
                                disabled={isSavingProfile || isSavingSignature || isFieldsDisabled}
                                data-testid="profile-save-submit"
                                className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-primary-foreground bg-primary hover:bg-primary/90 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-ring disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                {isSavingProfile || isSavingSignature ? (
                                    <>
                                        <div className="motion-safe:animate-spin rounded-full h-4 w-4 border-b-2 border-primary-foreground mr-2"></div>
                                        {t('profile.saving')}
                                    </>
                                ) : (
                                    t('common:actions.save')
                                )}
                            </button>
                        </div>
                    </form>
                </div>
            </div>
    )
}